import { sva } from '@allenai/varnish-panda-runtime/css';
import type { RecipeVariantProps } from '@allenai/varnish-panda-runtime/types';

/**
 *  There is no native HTML element with switch styling. <input type="checkbox"> is the closest
 *  semantically, but isn't styled or exposed to assistive technology as a switch. Switch helps
 *  achieve accessible switches that can be styled as needed.
 *
 *  See: https://react-spectrum.adobe.com/react-aria/Switch.html
 */
const switchRecipe = sva({
    className: 'switch-recipe',
    slots: ['root', 'label', 'track', 'thumb', 'description', 'error', 'contentContainer'],
    base: {
        root: {
            display: 'flex',
            flexDirection: 'column',
            cursor: 'pointer',
            color: 'text',

            colorPalette: 'elements.overrides.form.switch',
            _disabled: {
                cursor: 'not-allowed',
                colorPalette: 'elements.overrides.form.switch.disabled',
            },
            _componentReadOnly: {
                cursor: 'default',
                pointerEvents: 'none',
            },
        },
        contentContainer: {
            display: 'flex',
            gap: '[8px]',
            alignItems: 'center',
        },
        label: {},
        track: {
            boxSizing: 'border-box',
            position: 'relative',
            flexShrink: '0',
            display: 'flex',
            alignItems: 'center',
            padding: '[1px]',
            borderRadius: 'full',
            border: '1px solid',
            borderColor: 'colorPalette.stroke',
            backgroundColor: 'colorPalette.fill',

            _groupSelected: {
                borderColor: 'transparent',
            },

            // Switch only has hover for un-selected enabled state
            _groupHoverNotDisabledNotSelected: {
                borderColor: 'colorPalette.hovered.stroke',
            },
        },
        thumb: {
            boxSizing: 'border-box',
            position: 'absolute',
            display: 'block',
            aspectRatio: '1',
            borderRadius: 'full',
            transition: '[right 0.2s ease-in-out]',

            backgroundColor: 'colorPalette.stroke',

            _groupSelected: {
                right: '[1px]', // position thumb when selected
                backgroundColor: 'colorPalette.contrast',
            },

            // Switch only has hover for un-selected enabled state
            _groupHoverNotDisabledNotSelected: {
                backgroundColor: 'colorPalette.hovered.stroke',
            },
        },
        description: {},
        error: {},
    },
    variants: {
        size: {
            small: {
                root: {
                    fontSize: '[12px]',
                },
                track: {
                    width: '[1.375rem]',
                    height: '[0.75rem]',
                },
                thumb: {
                    width: '[0.5rem]',
                    // 100% - width - padding
                    right: '[calc(100% - 0.5rem - 1px)]',
                },
            },
            medium: {
                root: {
                    fontSize: 'sm',
                },
                track: {
                    width: '[1.625rem]',
                    height: '[0.875rem]',
                },
                thumb: {
                    width: '[0.625rem]',
                    // 100% - width - padding
                    right: '[calc(100% - 0.625rem - 1px)]',
                },
            },
            large: {
                root: {
                    fontSize: 'md',
                },
                track: {
                    width: '[2.3125rem]',
                    height: '[1.25rem]',
                },
                thumb: {
                    width: '[0.875rem]',
                    // 100% - width - padding
                    right: '[calc(100% - 0.875rem - 2px)]',
                    _groupSelected: {
                        right: '[2px]',
                    },
                },
            },
        },
        color: {
            default: {
                root: {
                    _selected: {
                        _notDisabled: {
                            colorPalette: 'elements.overrides.form.switch.checked',
                        },
                    },
                },
            },
            primary: {
                root: {
                    _selected: {
                        _notDisabled: {
                            colorPalette: 'elements.primary',
                        },
                    },
                },
            },
            secondary: {
                root: {
                    _selected: {
                        _notDisabled: {
                            colorPalette: 'elements.secondary',
                        },
                    },
                },
            },
            tertiary: {
                root: {
                    _selected: {
                        _notDisabled: {
                            colorPalette: 'elements.tertiary',
                        },
                    },
                },
            },
        },
    },
    defaultVariants: {
        size: 'medium',
        color: 'primary',
    },
});

type SwitchRecipeProps = RecipeVariantProps<typeof switchRecipe>;

export { switchRecipe, switchRecipe as default };
export type { SwitchRecipeProps };
